import React, {useCallback, useEffect, useState} from 'react';
import I18n from 'app/i18n';
import {Link} from 'react-scroll';
import {getLinkPath, flagImage} from 'app/utils';
import {setLanguageStorage} from 'app/utils/Language';
import {switchLanguage} from 'app/utils/langs/switchLanguage';
import {LOGO_IMAGE} from 'app/consts';
import FooterPartnerList from 'site_version/common/FooterPartnerList';
import LandingFaq from 'app/components/landing_components/LandingFaq';
import GrandSlamLandingQuestion from 'app/components/landing_grand_slam/GrandSlamLandingQuestion';
import Grid from 'app/components/landing_grand_slam/TournamentGrid/Grid';
import Get from 'app/utils/Get';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';

const ADDITIONAL_PARTNER_LIST = [
    {
        icon_height: '32px',
        icon_width: '54px',
        svg: 'qiwi'
    },
    {
        icon_height: '32px',
        icon_width: '72px',
        svg: 'youmoney'
    },
    {
        icon_height: '32px',
        icon_width: '48px',
        svg: 'pix'
    },
    {
        icon_height: '32px',
        icon_width: '72px',
        svg: 'grabpay'
    }
];

const LINK_OFFSET = -79;
const LINK_DURATION = 400;

const HEADER_LINKS = [
    'Bracket',
    'Questions',
    'Faq'
];

const GrandSlamLanding: React.FC = () => {
    const [tournament, setTournamnet] = useState(null);
    const [isGridOpen, setIsGridOpen] = useState(false);
    const [activeLang, setActiveLang] = useState(localStorage.getItem('language'));

    const isSingleElimination = tournament?.tournament_type === 'single_elimination';
    const gridClases = `landing-promo-tournament-grid 
        ${isGridOpen ? 'grid-active' : ''} 
        ${isSingleElimination ? `single single-${tournament?.participants_count}` : ''}`;

    const toggleGradeOpen = function() {
        setIsGridOpen((prevState: boolean) => !prevState);
    };

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        new Get({url: getLinkPath('/grand-slams')})
            .execute()
            .then(response => response.json())
            .then(response => {
                setTournamnet(response);
            });
    }, []);

    useEffect(() => {
        setActiveLang(localStorage.getItem('language'));
    }, [activeLang]);

    const changeLangHandler = (e: React.MouseEvent<HTMLButtonElement>, lang: string) => {
        setActiveLang(lang);
        setLanguageStorage(lang);
        switchLanguage(e as unknown as Event, lang);
    };

    const changeLangHandlerEn = useCallback((e: React.MouseEvent<HTMLButtonElement>) => changeLangHandler(e, 'en'), [activeLang]);
    const changeLangHandlerRu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => changeLangHandler(e, 'ru'), [activeLang]);

    const renderHeaderLinks = () => HEADER_LINKS.map(link => <span className="menu-navigation-item" key={`headerLink-${link}`}>
        <Link
            className="link-menu"
            activeClass="active"
            to={`nav${link}`}
            spy
            smooth
            isDynamic
            offset={LINK_OFFSET}
            duration={LINK_DURATION}
        >{I18n.t(`menu_links.${link.toLowerCase()}`)}</Link>
    </span>);

    return <>
        <div className="landing-promo-header grand-slam">
            <div className="landing-promo-header-inner">
                <a href={getLinkPath('/')} className="logo-landing-promo">
                    <img src={LOGO_IMAGE} alt="EGB.com" title="EGB.COM"/>
                </a>
                <div className="menu-navigation">
                    {renderHeaderLinks()}
                </div>
                <div className="menu-lang">
                    <button className={`menu-lang-item${activeLang === 'ru' ? ' active' : ''}`} onClick={changeLangHandlerRu} title="RU">
                        <span className="icon">{flagImage('ru')}</span>
                    </button>
                    <button className={`menu-lang-item${activeLang === 'en' ? ' active' : ''}`} onClick={changeLangHandlerEn} title="EN">
                        <span className="icon">{flagImage('gb')}</span>
                    </button>
                </div>
            </div>
        </div>
        <main className="landing-promo-content grand-slam" id="navGrandSlam">
            <div className="landing-promo-top">
                <div className="landing-promo-top-inner">
                    <div className="landing-promo-title">
                        <span className="part-first">{I18n.t('grand_slam.title_landing_1')}</span>
                        <h1 className="part-second">{I18n.t('grand_slam.title_landing_2')}</h1>
                    </div>
                    <div className="landing-promo-section banner">
                        <span className="section-big-title">{I18n.t('grand_slam.prize_fund')}</span>
                    </div>
                    <div className="landing-promo-tournir-description">
                        <div className="landing-promo-tournir-icon">
                            <SpriteIcon name="#svg-gold-cup" className="svg-default"/>
                        </div>
                        <p className="landing-promo-tournir-text">
                            {I18n.t('grand_slam.tournament_description')}
                        </p>
                    </div>
                </div>
            </div>
            <div className={gridClases} id="navBracket">
                <div className="tournament-grid-top">
                    <div className="tournament-grid-top-inner">
                        <h2 className="tournament-grid-top-title">{I18n.t('grand_slam.tournament_bracket')}</h2>
                        <button className={`tournament-grid-top-opener ${isGridOpen ? 'active' : ''}`} onClick={toggleGradeOpen}>
                            <SpriteIcon name="#svg-arrow-for-menu" className="svg-default"/>
                        </button>
                    </div>
                </div>
                <Grid
                    winnerMatches={tournament?.winner_matches}
                    loserMatches={tournament?.loser_matches}
                    tournamentType={tournament?.tournament_type}
                    participantsCount={tournament?.participants_count}
                />
            </div>
            <div className="landing-promo-faq landing-promo-questions" id="navQuestions">
                <div className="landing-promo-middle-inner">
                    <h3>{I18n.t('menu_links.questions')}</h3>
                    <GrandSlamLandingQuestion questions={tournament?.questions}/>
                </div>
            </div>
            <div className="landing-promo-faq" id="navFaq">
                <div className="landing-promo-middle-inner">
                    <h3>{I18n.t('menu_links.faq')}</h3>
                    <LandingFaq landingName="grand_slam" numberOfQuestions={8}/>
                </div>
            </div>
            <div className="landing-promo-footer">
                <div className="landing-promo-footer-inner">
                    <FooterPartnerList additionalPartners={ADDITIONAL_PARTNER_LIST}/>
                    <div className="landing-promo-footer-rules">
                        <div className="row">
                            <a
                                className="landing-promo-rules"
                                href={getLinkPath('/terms_and_conditions')}
                            >
                                {I18n.t('advertising.terms_conditions')}
                            </a>
                        </div>
                        <div className="row">
                            <a
                                className="landing-promo-rules"
                                href={getLinkPath('/privacy_policy')}
                            >
                                {I18n.t('privacy_policy')}
                            </a>
                        </div>
                        <div className="row">
                            <a href={getLinkPath('/')} className="logo-landing-promo">
                                <img src={LOGO_IMAGE} alt="EGB.com" title="EGB.COM"/>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </>;
};

export default GrandSlamLanding;
